// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SettingLevel.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { filter, isNil, isEmpty } from 'lodash/fp';
import { observable, action, computed } from 'mobx';
import AuthUtils from 'share/utils/Izenda.AuthUtils';
import settingConstant from '../data/Izenda.SettingConstants';
import uiState from 'logic/Izenda.UiState';
import AppStore from 'logic/Izenda.AppStore';

class SettingLevel {
  @observable tenants = [];

  @computed
  get selectedTenant() {
    return uiState.tenantId;
  }
  @computed
  get disableRoleDropdown() {
    return this.disableSystemSetting || uiState.onlyShowTenant || !uiState.currentUser.systemAdmin;
  }
  @computed
  get disableSystemSetting() {
    return uiState.disableSystemSetting || uiState.systemMode;
  }

  constructor(store: any) {
    this.store = store;
    this.availableLevels = filter(
      (availableLevel: any) => uiState.currentUser.isSystemUser || availableLevel.value !== 'System',
      settingConstant.availableLevels
    );
  }

  /**
   * get selected level
   */
  selectedLevel() {
    return uiState.tenantId || uiState.onlyShowTenant ? 'Tenant' : 'System';
  }

  /**
   * show tenants
   */
  showTenants() {
    return this.selectedLevel() === 'Tenant';
  }

  /**
   * Load initial
   * @param {number} systemMode
   */
  @action('loadInitial')
  loadInitial(systemMode: number) {
    this.readSystemMode(systemMode);
  }

  /**
   * Read system mode
   * @param {number} systemMode
   */
  @action('readSystemMode')
  readSystemMode(systemMode: number) {
    this.systemMode = systemMode;
    uiState.systemMode = systemMode;
  }

  /**
   * Read tenants
   * @param {boolean} shouldLoadDefaultTenant
   * @param {array} tenants
   */
  @action('readTenants')
  readTenants(shouldLoadDefaultTenant: boolean, tenants: Array) {
    this.tenants = tenants;
    if (shouldLoadDefaultTenant && isNil(this.selectedTenant)) {
      this.setSelectedTenant(isEmpty(this.tenants) ? null : this.tenants[0].id);
    }
  }

  /**
   * Change tenant
   */
  changeTenant(change: any) {
    this.setSelectedTenant(change.value);
  }

  /**
   * Set tenant ID of UIState
   */
  setSelectedTenant(tenantId: string) {
    const currentTenantId = uiState.tenantId;
    uiState.setTenantId(tenantId);
    // Store in cookie
    !!tenantId ? AuthUtils.setSelectedTenant(tenantId) : AuthUtils.removeSelectedTenant();

    if (tenantId !== currentTenantId) {
      AppStore.loadAppSettings();
    }
  }
}

export default SettingLevel;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.SettingLevel.js